import type { CreationSetup, CutTransition, EditDoc, FormatId, OpInput, SourceKind } from "@editit/editdoc";

/**
 * Le montage (EditDoc), ses opérations et ses listes fermées viennent de @editit/editdoc : les types
 * par `import type` (rien dans le bundle), les listes et petits calculs par `/core` (sans zod). Jamais recopiés.
 */
export type { Adjust, CutTransition, EditDoc, Emphasis, FilterId, FormatId, Frame, Layer, LayerStyle, Segment,   SourceKind,    } from "@editit/editdoc";
export { ADJUST_KEYS, CUT_TRANSITIONS, FILTER_IDS, FORMAT_IDS, UPLOAD_KINDS as SOURCE_KINDS, TEXT_FONTS } from "@editit/editdoc/core";
/** Une opération telle que le client l'envoie (les défauts sont posés par l'API). */
export type Op = OpInput;
export type Rotation = NonNullable<import("@editit/editdoc").Frame["rotate"]>;
export type TextAlign = NonNullable<import("@editit/editdoc").LayerStyle["align"]>;
import { lang } from "./i18n.js";
import { config } from "../app/env.js";
import { forgetScreens, writeScope, type WriteScope } from "./keep.js";

/**
 * Le client typé de l'API /v1, écrit d'après apps/api/CONTRACT.md (l'OpenAPI dit le détail).
 * Tout passe par `request` : en-tête d'authentification, erreurs lisibles, JSON.
 */

// ——— Les types du contrat ———————————————————————————————————————————————

export type Intensity = "sobre" | "equilibre" | "energique";
type Stage = "queued" | "transcribing" | "preview" | "dressing" | "checking" | "ready" | "failed";

/** Le lecteur v2 (GET …/versions/{v}/review) : calques + une vidéo par source + SFX. */
export type ReviewBundle = {
  format: FormatId;
  width: number;
  height: number;
  duration: number;
  html: string;
  sources: { id: string; kind: string; url: string }[];
  /** `rate` : la vitesse du plan (speed), quand l'API la donne ; la vidéo joue alors à cette vitesse. */
  shots: { k: number; start: number; duration: number; sourceId: string; in: number; rate?: number; voice?: { sourceId: string; in: number }; visual?: { sourceId: string; start: number; duration: number; in: number; rate: number }[] }[];
  sfx: { t: number; url: string; volume: number }[];
  /** La musique de fond, par passages (temps de montage), jouée avec la vidéo. */
  music?: { start: number; duration: number; in: number; volume: number; url: string }[];
  /**
   * Les voix off posées en piste : leur son, joué comme la musique (`in` = instant dans le fichier). Leur image
   * filmée (`camera`) est une case `.vo-cam` de la page, qui la joue elle-même. Absentes d'une API plus ancienne.
   */
  voiceovers?: { start: number; duration: number; in: number; volume: number; url: string; camera?: { key: string } }[];
  /** Le son des vidéos incrustées (leur vidéo reste muette dans la page), joué comme une voix off. */
  overlayAudio?: { layerId: string; start: number; duration: number; in: number; volume: number; url: string }[];
  /**
   * D8 : la part de la vidéo que ce paquet joue ; TOUS ses temps partent de `from` (position
   * dans la vidéo = from + t). Absente d'une API plus ancienne : toute la vidéo.
   */
  window?: { from: number; to: number; total: number };
  /** La zone libre (px du format) où la grille 3 × 3 cale un élément déplacé. Absente d'une API plus ancienne. */
  free?: { x: number; y: number; w: number; h: number };
  /** Les sources jouées sur l'original, faute de copie légère (lourd : on le signale). */
  fallbacks?: string[];
};

/** L'insigne d'un espace (sélecteur) : ses comptes sociaux (photo, sinon logo du réseau), icône du site ; vide → l'initiale. */
export type WorkspaceBadge = { accounts?: { platform: string; avatar?: string }[]; avatars: string[]; siteIcon?: string };
export type Me = { uid: string; workspaces: { id: string; name: string; role: string; badge?: WorkspaceBadge }[] };

export type Project = {
  id: string;
  workspaceId: string;
  title: string;
  /** Une séquence (30/10) : un autre montage du même projet, copié du montage d'origine `sequenceOf` ; son nom. */
  sequenceOf?: string;
  sequenceName?: string;
  format: FormatId;
  status: string;
  stage?: Stage;
  stageMessage?: string;
  generation?: { step: string; startedAt: string; stepStartedAt: string };
  currentVersion: number;
  storyboardRevision?: number;
  /** « carousel » : une publication en images (features/carousel) ; absent : une vidéo. */
  kind?: "video" | "carousel";
  carouselRevision?: number;
  previewVersion?: number | null;
  v1Version?: number | null;
  approvedVersion?: number | null;
  lastRender?: { jobId: string; version: number; status: JobStatus; downloadUrl?: string } | null;
  brief?: Brief | null;
  /** Le style choisi à la création (template d'Inspiration) ; absent d'une API plus ancienne. */
  style?: ProjectStyle;
  /** Vignette du montage (image tirée de l'aperçu), URL signée. */
  thumbnailUrl?: string | null;
  /** Le script écrit pour cette vidéo (features/script) : seul son état sert aux listes. */
  script?: { status: string } | null;
  /** Le titre a été donné par editit (hook, première phrase) : la personne ne l'a pas choisi. */
  titleAuto?: boolean;
  /** Créé par le parcours de création et pas encore lancé : absent de « Mes montages » jusqu'au lancement. */
  unlisted?: boolean;
  /** Pourquoi le montage n'a pas abouti, en clair (quand stage = failed). */
  error?: string;
  /** Le même échec, en mots de montage, avec l'action qui le règle (doc 13, A15) ; absent d'une API plus ancienne. */
  failure?: Failure | null;
  /** Durée de la vidéo montée, en secondes, quand l'API la donne. */
  duration?: number;
  /** La place prise par les fichiers déposés, en octets (liste des montages) ; absent d'une API plus ancienne. */
  sizeBytes?: number;
  createdAt: string;
  updatedAt: string;
};

/** `showcase` : une vitrine, montée sans caméra (captures, plans de coupe) — sinon l'API refuse (409 no_main_cam). */
export type Brief = { format: FormatId; intensity: Intensity; message?: string; name?: string; role?: string; lang?: string; templateId?: string; lookId?: string; captionPresetId?: string; kitId?: string; showcase?: boolean; creation?: CreationSetup; tier?: { id: string; provider: string; model: string } };

/**
 * Un échec dit pour la personne : `message` en mots de montage (« Je n'entends personne parler
 * dans cette vidéo »), `action` ce qui le règle (`showcase`, `reupload`, `relabel`, `shorter`,
 * `retry`), avec son libellé. Une chaîne seule est acceptée aussi (le code).
 */
type Failure = { code: string; message: string; action?: { code: string; label?: string } | string | null };

type JobStatus = "queued" | "running" | "done" | "failed";
export type Job = {
  id: string;
  kind: "pipeline" | "render" | "revise" | "export";
  projectId: string;
  version?: number;
  format?: FormatId;
  status: JobStatus;
  stage?: string;
  error?: string;
  failure?: Failure | null;
  downloadUrl?: string;
  srtUrl?: string;
  /** L'avancement réel d'un rendu (0 → 1), lu par le worker ; absent tant que rien n'est mesuré. */
  progress?: number;
  resolution?: ExportResolution;
  fps?: ExportFps;
  /** Ce que le travail a produit : pour des retours, la version qu'il a posée (`version` est celle de départ). */
  result?: { version?: number };
  createdAt: string;
  updatedAt: string;
};

/** Les réglages d'un export : hd = la taille du format, 4k = deux fois plus (pas en 4:5). */
export type ExportResolution = "hd" | "4k";
export type ExportFps = 24 | 30 | 60;
export const EXPORT_FPS: ExportFps[] = [24, 30, 60];
/**
 * Le son du commerce à l'export (31/10) : `video`, son extrait part dans la vidéo — usage personnel (par défaut) ; `app`,
 * la vidéo part sans lui, à ajouter dans TikTok ou Instagram (`commercialSong` de POST …/exports).
 */
export type SongExport = "video" | "app";
export const SONG_EXPORTS: SongExport[] = ["video", "app"];
/** Les formats qui ont une 4K (HyperFrames rend la 4K en multiple entier du format ; le 4:5 n'en a pas). */
export const UHD_FORMATS: FormatId[] = ["vertical-talking-head", "vertical-split-card", "landscape-youtube", "square-feed"];

/**
 * La réponse à une demande d'envoi. `duplicate` : ce fichier (même empreinte) est déjà une
 * source du projet ; `source.uploaded` → rien à envoyer, sinon l'adresse (ou les morceaux)
 * pour reprendre.
 */
type TicketBase = { sourceId: string; uri: string; duplicate?: boolean; source?: SourceInfo };
export type UploadTicket =
  | (TicketBase & { method: "PUT"; uploadUrl: string; multipart?: undefined })
  | (TicketBase & { multipart: { uploadId: string; partSize: number; partCount: number }; uploadUrl?: undefined })
  | (TicketBase & { duplicate: true; uploadUrl?: undefined; multipart?: undefined });

/**
 * Un rush du projet. `durationSec`, `thumbnailUrl` et `ready` arrivent avec la refonte
 * créateurs (CONTRACT.md) : l'app s'en passe tant que l'API ne les donne pas.
 */
export type SourceInfo = { id: string; kind: SourceKind; filename: string; size: number; uploaded?: boolean; ready?: boolean; durationSec?: number; thumbnailUrl?: string | null; durationUnknown?: boolean; prepareJobId?: string; prepared?: { wordsKey?: string }; tag?: SourceTag };
/** Ce qu’est un plan et ce qu’il montre : vu par l’IA à l’envoi, corrigé par la personne. */
export type SourceTag = { kind: "parole" | "plan" | "ecran" | "image"; summary: string; keywords: string[]; by: "ia" | "utilisateur" };

/** `ops` : les opérations de la version qui font ce changement ; vide = il ne s'annule pas seul. */
export type Change = { id: string; text: string; status: "done" | "not_done"; why?: string; ops?: number[] };
export type VersionInfo = { version: number; label?: string; author?: { by: string; uid?: string } | string; createdAt: string; changes?: Change[] };

export type Version = {
  projectId: string;
  version: number;
  doc: EditDoc;
  issues: { code: string; severity: "error" | "warning"; message: string }[];
  author: { by: string; uid?: string };
  createdAt: string;
  changes?: Change[];
};

/** Une animation posée depuis la feuille Éléments : la version, et si ses textes viennent de la vidéo ou de l'exemple. */
export type MotionPlaced = Version & { motion: { recipeId: string; layerId: string; start: number; adapted: "video" | "sample"; kept: number; reason?: "no_ai" | "no_speech" | "budget_exhausted" | "pending" | "failed"; later?: boolean } };
/** Les textes d'une animation posée, repris de ce qui se dit : la nouvelle version, ou pourquoi rien n'a changé. */
/** Lot E · les détourages de la personne qu'une version demande (GET/POST …/masks). */
export type MaskStatus = {
  state: "none" | "preparing" | "ready" | "failed";
  items: { sourceId: string; in: number; out: number; state: "missing" | "queued" | "running" | "ready" | "failed"; error?: string }[];
  masks: import("@editit/editdoc").PersonMask[];
};
export type MotionAdapted = MotionPlaced | { applied: false; reason: "no_ai" | "no_speech" | "budget_exhausted" | "pending" | "failed" | "gone" | "unchanged" };

/** `from`/`to` : le passage visé (secondes de la vidéo montée) ; sans eux mais avec `t` : le plan à cet instant. */
export type FeedbackItem = { t?: number; from?: number; to?: number; text?: string; voiceKey?: string; pin?: { x: number; y: number; layerId?: string } };
// ——— La refonte créateurs (CONTRACT.md, dernière section) ————————————————

/**
 * Un morceau d'image d'un plan remplacé : `start`/`duration` en secondes de la vidéo montée, `mediaIn`/`mediaOut` dans le
 * fichier qui donne l'image (`replacement` : la vidéo posée, sinon le rush du plan).
 */
type ClipPiece = { sourceId: string; start: number; duration: number; mediaIn: number; mediaOut: number; replacement: boolean };

/** La timeline d'une version, tout en temps de montage (secondes). */
export type Timeline = {
  duration: number;
  tracks: {
    video: {
      segmentId: string;
      sourceId: string;
      start: number;
      duration: number;
      in: number;
      out: number;
      keep: boolean;
      thumbUrl?: string;
      text: string;
      rate?: number;
      transition?: CutTransition;
      /** L'image remplacée d'un plan, morceau par morceau (packages/engine timeline, `visualPieces`) ; absents sinon. */
      pieces?: ClipPiece[];
      /** La variante posée du plan (`segment.visual`), telle que le document la garde. */
      visual?: EditDoc["segments"][number]["visual"];
      /** Le passage du même rush retiré juste avant ce plan (secondes du rush) : le ciseau de la coupe le remet (`restore_cut`). */
      removedBefore?: { in: number; out: number };
    }[];
    /** Une ligne de sous-titres porte son plan et ses mots (`segmentId`, `fromWord`…`toWord`) : on la retape et la recale. */
    text: { id: string; kind: "hook" | "title" | "caption"; start: number; duration: number; text: string; segmentId?: string; fromWord?: number; toWord?: number; retimed?: boolean }[];
    /** `kind` (genre du calque) : range les plans de coupe sur leur piste ; absent d'une API plus ancienne. */
    overlays: { id: string; blockId: string; kind?: string; name: string; start: number; duration: number }[];
    audio: {
      voice: { start: number; duration: number }[];
      music?: MusicItem;
      /** Toutes les musiques, chacune à sa place (`main` puis celles posées en plus) ; absentes d'une API plus ancienne. */
      musics?: MusicItem[];
      sfx: { id: string; t: number; name: string }[];
      /**
       * Les voix off posées en piste (packages/engine timeline) : `in`/`out` en secondes du fichier, `words` en
       * secondes de la vidéo ; `camera` : l'image filmée et où elle se pose. Absentes d'une API plus ancienne.
       */
      voiceovers?: VoiceoverItem[];
    };
  };
  dropped: { segmentId: string; text: string; reason?: string }[];
  /** Toutes les lignes de sous-titres (supprimées comprises), pour la liste de l'éditeur ; absentes d'une API plus ancienne. */
  captions?: CaptionLineItem[];
};
export type CaptionLineItem = import("@editit/engine").CaptionLineItem;

/** Une musique de la timeline : `start`/`duration` sa place dans la vidéo, `offset` où elle est prise dans le fichier. */
export type MusicItem = { id?: string; title: string; start: number; duration: number; volume: number; uri?: string; offset?: number };

/** Une voix off de la timeline (même forme que `tracks.audio.voiceovers` de packages/engine). */
export type VoiceoverItem = { id: string; sourceId: string; uri: string; name: string; start: number; duration: number; in: number; out: number; volume: number; words: { text: string; start: number; end: number }[]; camera?: { layout: VoiceOverLayout } };
export type VoiceOverLayout = import("@editit/editdoc").VoiceOverLayout;

export type ProjectStyle = { templateId?: string; lookId?: string; captionPresetId?: string; kitId?: string };

// ——— Le transport ————————————————————————————————————————————————————

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** L'itinéraire n'existe pas (encore) côté serveur : réponse 404 qui n'est pas du JSON de l'API. */
    readonly missing = false,
    /** Les règles du monteur que le changement ne respecte pas (422 de `…/ops`). */
    readonly issues: { code: string; severity?: string; message: string }[] = [],
    /** Le motif, pour la machine (`{ error, code }`) : « too_large », « not_uploaded », « no_main_cam », « busy »… */
    readonly code?: string,
  ) {
    super(message);
  }
}

/**
 * Vrai quand l'API ne sert pas (encore) cet itinéraire : la refonte avance côté serveur en
 * parallèle, l'app cache alors la fonction au lieu d'afficher une erreur.
 */
export function isMissing(e: unknown): boolean {
  return e instanceof ApiError && e.missing;
}

type TokenSource = () => Promise<string | null>;
let authorization: TokenSource = async () => null;
/** Branché par auth.ts : « Bearer <idToken> » en ligne, « Dev dev » en local. */
export function setAuthorization(source: TokenSource) {
  authorization = source;
}

function apiUrl(path: string): string {
  return config.apiUrl + path;
}

/**
 * En dev, l'API locale signe ses URL de médias vers elle-même (http://localhost:8080/dev/media/…) :
 * on les fait passer par le relais Vite pour rester sur la même origine (pas de CORS en local).
 */
export function mediaUrl(url: string): string {
  if (config.apiUrl !== "") return url;
  const m = /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/dev\/media\/.*)$/.exec(url);
  return m ? m[3] : url;
}

async function headers(extra?: Record<string, string>): Promise<Record<string, string>> {
  const h: Record<string, string> = { "accept-language": lang, ...extra };
  const a = await authorization();
  if (a) h.authorization = a;
  return h;
}

/** Un fichier binaire de l'API (export ZIP…), avec la même authentification et les mêmes erreurs. */
export async function requestBlob(path: string): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), { headers: await headers() });
  } catch {
    throw new ApiError("network", 0);
  }
  if (!res.ok) {
    const json = await res.json().catch(() => undefined) as { error?: string; code?: string } | undefined;
    throw new ApiError(json?.error ?? `HTTP ${res.status}`, res.status, false, [], json?.code);
  }
  return res.blob();
}

/**
 * Lectures parties en avance (prefetch) : l'écran qui les demande ensuite reprend la réponse en route au lieu
 * d'en relancer une. Servie une seule fois, oubliée après 10 s (jamais une donnée vieille).
 */
const early = new Map<string, { pending: Promise<unknown>; uses: number }>();
/** Une lecture d'avance que cette écriture rend fausse : celles du montage touché (et des listes, si sa carte change). */
function staleEarly(scope: WriteScope, path: string): boolean {
  if (scope.all) return true;
  if (scope.project !== null) {
    const base = `/v1/projects/${encodeURIComponent(scope.project)}`;
    if (path === base || path.startsWith(`${base}/`) || path.startsWith(`${base}?`)) return true;
  }
  return scope.lists && path.startsWith("/v1/workspaces/");
}
/** `uses` : combien d'écrans la reprennent (le projet ouvert est lu par l'espace et par l'écran du projet). */
export function prefetch(path: string, uses = 1) {
  if (early.has(path)) return;
  const entry = { pending: request("GET", path), uses };
  early.set(path, entry);
  const drop = () => early.get(path) === entry && early.delete(path);
  entry.pending.catch(drop);
  setTimeout(drop, 10_000);
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const ahead = method === "GET" ? early.get(path) : undefined;
  if (ahead) {
    if (--ahead.uses <= 0) early.delete(path);
    return ahead.pending as Promise<T>;
  }
  // Une écriture : les lectures d'avance qu'elle rend fausses ne sont plus servies (celles d'un autre montage, si).
  if (method !== "GET") {
    const scope = writeScope(path);
    for (const k of [...early.keys()]) if (staleEarly(scope, k)) early.delete(k);
  }
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method,
      headers: await headers(body === undefined ? undefined : { "content-type": "application/json" }),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError("network", 0);
  }
  const text = await res.text();
  let json: unknown = undefined;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    /* pas du JSON */
  }
  if (!res.ok) {
    const msg = json && typeof json === "object" && "error" in json ? String((json as { error: unknown }).error) : `HTTP ${res.status}`;
    const issues = json && typeof json === "object" && Array.isArray((json as { issues?: unknown }).issues) ? (json as { issues: ApiError["issues"] }).issues : [];
    const code = json && typeof json === "object" && typeof (json as { code?: unknown }).code === "string" ? (json as { code: string }).code : undefined;
    throw new ApiError(msg, res.status, res.status === 404 && json === undefined, issues, code);
  }
  if (method !== "GET") forgetScreens(path);
  return json as T;
}

/** Récupère un texte (le lecteur HTML, le .srt) avec l'authentification. */
export async function requestText(path: string): Promise<string> {
  const res = await fetch(apiUrl(path), { headers: await headers() }).catch(() => {
    throw new ApiError("network", 0);
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let isJson = false;
    try {
      msg = String(JSON.parse(text).error ?? msg);
      isJson = true;
    } catch {
      /* rien */
    }
    throw new ApiError(msg, res.status, res.status === 404 && !isJson);
  }
  return text;
}

const enc = encodeURIComponent;

/** L'adresse d'une version : la même pour la lire, sa timeline, son lecteur, et pour les demander d'avance (`prefetch`). */
export const versionPath = (id: string, v: number) => `/v1/projects/${enc(id)}/versions/${v}`;

/**
 * L'éditeur rouvert par son adresse (`/m/{id}/montage?v=N`) : sa version, sa timeline, sa vidéo et la liste des versions
 * partent avec le projet, au lieu d'attendre qu'il arrive puis que le code de l'éditeur se charge.
 */
export function warmEditor(id: string, v: number) {
  const base = versionPath(id, v);
  for (const path of [base, `${base}/timeline`, `${base}/review`, `/v1/projects/${enc(id)}/versions`]) prefetch(path);
}

// ——— Les appels, dans l'ordre du parcours ———————————————————————————————

export const api = {
  me: () => request<Me>("GET", "/v1/me"),
  /** Les espaces : un par marque (le sélecteur en haut à droite). */
  createWorkspace: (name: string) => request<Me["workspaces"][number]>("POST", "/v1/workspaces", { name }),
  renameWorkspace: (wid: string, name: string) => request<Me["workspaces"][number]>("PATCH", `/v1/workspaces/${enc(wid)}`, { name }),
  projects: (wid: string) => request<{ projects: Project[] }>("GET", `/v1/workspaces/${enc(wid)}/projects`),
  /** Le style part avec la création (refonte) ; une API plus ancienne l'ignore, le brief le redit. */
  /** `unlisted` : le projet du parcours de création, qui n'entre dans « Mes montages » qu'au lancement. */
  createProject: (workspaceId: string, title: string, format: FormatId, style: ProjectStyle & { kind?: Project["kind"]; unlisted?: boolean } = {}) => request<Project>("POST", "/v1/projects", { workspaceId, title, format, ...style }),
  project: (id: string) => request<Project>("GET", `/v1/projects/${enc(id)}`),
  renameProject: (id: string, title: string) => request<Project>("PATCH", `/v1/projects/${enc(id)}`, { title }),
  deleteProject: (id: string) => request<{ ok: boolean }>("DELETE", `/v1/projects/${enc(id)}`),
  /** La copie d'un montage : ses fichiers, ses versions (annuler compris) ; 409 pendant un montage. */
  duplicateProject: (id: string) => request<Project>("POST", `/v1/projects/${enc(id)}/duplicate`),
  /** Les séquences d'un montage (30/10) : le montage d'origine puis ses autres timelines. */
  sequences: (id: string) => request<{ sequences: Project[] }>("GET", `/v1/projects/${enc(id)}/sequences`),
  /** Une nouvelle séquence, copie de ce montage ; sans nom : « Séquence N ». */
  createSequence: (id: string, name?: string) => request<Project>("POST", `/v1/projects/${enc(id)}/sequences`, name ? { name } : {}),

  /** `sha256` : l'empreinte des 8 premiers Mio ; l'API reconnaît un fichier déjà déposé (doc 13, A6). */
  upload: (id: string, file: { filename: string; contentType: string; size: number; kind?: SourceKind; sha256?: string }) =>
    request<UploadTicket>("POST", `/v1/projects/${enc(id)}/uploads`, file),
  uploadParts: (id: string, sourceId: string, uploadId: string, partNumbers: number[]) =>
    request<{ urls: Record<string, string> }>("POST", `/v1/projects/${enc(id)}/uploads/${enc(sourceId)}/parts`, { uploadId, partNumbers }),
  completeUpload: (id: string, sourceId: string, uploadId: string, parts: { partNumber: number; etag: string }[]) =>
    request<unknown>("POST", `/v1/projects/${enc(id)}/uploads/${enc(sourceId)}/complete`, { uploadId, parts }),
  /** Après un envoi en une seule requête : l'API vérifie que le fichier est bien arrivé (409 not_uploaded sinon). */
  confirmUpload: (id: string, sourceId: string) => request<unknown>("POST", `/v1/projects/${enc(id)}/uploads/${enc(sourceId)}/complete`, {}),
  /** Abandonne un envoi par morceaux laissé en plan (rush retiré pendant l'envoi). */
  abortUpload: (id: string, sourceId: string) => request<unknown>("POST", `/v1/projects/${enc(id)}/uploads/${enc(sourceId)}/abort`, {}),
  sources: (id: string) => request<{ sources: SourceInfo[] }>("GET", `/v1/projects/${enc(id)}/sources`),
  setSourceKind: (id: string, sid: string, kind: SourceKind) => request<unknown>("PATCH", `/v1/projects/${enc(id)}/sources/${enc(sid)}`, { kind }),
  setSourceTag: (id: string, sid: string, tag: Omit<SourceTag, "by">) => request<SourceInfo>("PATCH", `/v1/projects/${enc(id)}/sources/${enc(sid)}`, { tag }),
  /** Retire un rush du projet (409 « busy » pendant un montage). */
  deleteSource: (id: string, sid: string) => request<{ ok: boolean }>("DELETE", `/v1/projects/${enc(id)}/sources/${enc(sid)}`),

  /** Annule le montage en cours ; les fichiers et les choix restent, on peut relancer. */
  cancel: (id: string) => request<Project>("POST", `/v1/projects/${enc(id)}/cancel`),
  /** `idempotencyKey` : un double clic (ou un renvoi réseau) rend le même lancement. */
  start: (id: string, brief: Brief, idempotencyKey?: string) => request<Job>("POST", `/v1/projects/${enc(id)}/start`, { brief, ...(idempotencyKey ? { idempotencyKey } : {}) }),

  versions: (id: string) => request<{ versions: VersionInfo[] }>("GET", `/v1/projects/${enc(id)}/versions`),
  version: (id: string, v: number) => request<Version>("GET", versionPath(id, v)),
  playerHtml: (id: string, v: number) => requestText(`/v1/projects/${enc(id)}/versions/${v}/player`),
  /** `format` : la même version composée dans un autre format (aperçu d'un export 16:9 d'un projet 9:16). */
  /** `win` : une fenêtre de la vidéo (D8, secondes de la vidéo entière) ; sans elle, l'API choisit (tout, ou les 5 premières minutes). */
  review: (id: string, v: number, format?: FormatId, win?: { from: number; to?: number }) => {
    const qs = new URLSearchParams();
    if (format) qs.set("format", format);
    if (win) {
      qs.set("from", String(win.from));
      if (win.to !== undefined) qs.set("to", String(win.to));
    }
    const s = qs.toString();
    return request<ReviewBundle>("GET", `${versionPath(id, v)}/review${s ? `?${s}` : ""}`);
  },
  srt: (id: string, v: number) => requestText(`/v1/projects/${enc(id)}/versions/${v}/srt`),

  voiceNote: (id: string, contentType: string, size: number) => request<{ key: string; uploadUrl: string }>("POST", `/v1/projects/${enc(id)}/voice-notes`, { contentType, size }),
  feedback: (id: string, version: number, items: FeedbackItem[]) => request<Job>("POST", `/v1/projects/${enc(id)}/feedback`, { version, items }),
  ops: (id: string, v: number, ops: Op[]) => request<Version>("POST", `/v1/projects/${enc(id)}/versions/${v}/ops`, { ops }),
  /** L'écoute d'un son d'origine de template (Shazam, serveur) : le morceau du commerce reconnu, ou null (son original). */
  recognizeSound: (wid: string, uri: string, start: number) =>
    request<{ song: { title: string; artist: string; appleMusicUrl?: string; artworkUrl?: string; offset: number } | null }>("POST", `/v1/workspaces/${enc(wid)}/sounds/recognize`, { uri, start }),
  /** `adapt: false` : posée tout de suite avec ses textes d'exemple ; les siens se demandent ensuite (`motionAdapt`). */
  motion: (id: string, version: number, recipeId: string, at: number, adapt = true) => request<MotionPlaced>("POST", `/v1/projects/${enc(id)}/motion`, { version, recipeId, at, ...(adapt ? {} : { adapt: false }) }),
  masks: (id: string, version: number) => request<MaskStatus>("GET", `/v1/projects/${enc(id)}/masks?version=${version}`),
  prepareMasks: (id: string, version: number) => request<MaskStatus>("POST", `/v1/projects/${enc(id)}/masks`, { version }),
  motionAdapt: (id: string, layerId: string) => request<MotionAdapted>("POST", `/v1/projects/${enc(id)}/motion/adapt`, { layerId }),
  timeline: (id: string, v: number) => request<Timeline>("GET", `${versionPath(id, v)}/timeline`),
  /** Le contrôle du montage (trous noirs, passages trop courts et leur réparation) : des repères, jamais un blocage. */
  check: (id: string, v: number) => request<import("@editit/editdoc").CheckReport>("GET", `/v1/projects/${enc(id)}/versions/${v}/check`),
  saveAsTemplate: (id: string, name: string) => request<{ id: string }>("POST", `/v1/projects/${enc(id)}/save-as-template`, { name }),
  sourceFromStock: (id: string, stockId: string) => request<{ sourceId: string; uri: string }>("POST", `/v1/projects/${enc(id)}/sources/from-stock`, { stockId }),
  undo: (id: string, version: number, changeId: string) => request<Version>("POST", `/v1/projects/${enc(id)}/undo`, { version, changeId }),
  restore: (id: string, version: number) => request<Version>("POST", `/v1/projects/${enc(id)}/restore`, { version }),
  approve: (id: string, version: number) => request<unknown>("POST", `/v1/projects/${enc(id)}/approve`, { version }),

  exports: (
    id: string,
    version: number,
    formats?: FormatId[],
    settings: { resolution?: ExportResolution; fps?: ExportFps; commercialSong?: SongExport } = {},
  ) =>
    request<{ jobs: Job[] }>("POST", `/v1/projects/${enc(id)}/exports`, { version, formats, ...settings }),
  job: (id: string) => request<Job>("GET", `/v1/jobs/${enc(id)}`),
};
